import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../AuthContext';
import { useBranding } from '../../BrandingContext';
import { useTranslation } from 'react-i18next';

const currencyConfig = {
  uah: { field: 'price_uah', code: 'UAH' },
  rub: { field: 'price_rub', code: 'RUB' },
  usd: { field: 'price_usd', code: 'USD' }
};

// Кэш курсов валют
let currencyRatesCache = {
  UAH: 40.0,
  RUB: 100.0,
  USD: 1.0
};

// Загружаем курсы валют из API
const loadCurrencyRates = async () => {
  try {
    const response = await fetch('/api/public/currency-rates');
    if (response.ok) {
      const data = await response.json();
      currencyRatesCache = { ...currencyRatesCache, ...data.rates };
    }
  } catch (e) {
    console.error('Ошибка загрузки курсов валют:', e);
  }
};

// Загружаем курсы при инициализации
loadCurrencyRates();

// Функция конвертации баланса из USD в выбранную валюту
const convertBalanceFromUSD = (balanceUSD, targetCurrency) => {
  if (!balanceUSD || balanceUSD === 0) return 0;
  const currency = (targetCurrency || 'usd').toUpperCase();
  const rate = currencyRatesCache[currency] || 1.0;
  return balanceUSD * rate;
};

const featurePresets = {
  basic: ['Безлимитный трафик', 'До 5 устройств', 'Базовый анти-DPI'],
  pro: ['Приоритетная скорость', 'До 10 устройств', 'Ротация IP-адресов'],
  elite: ['VIP-поддержка 24/7', 'Статический IP по запросу', 'Автообновление ключей']
};

// Бейджи для tier'ов (может использоваться в будущем)
// Пример: { basic: 'Популярный', pro: 'ТОП-продажа', elite: 'Максимальная выгода' }
// eslint-disable-next-line no-unused-vars
const tierBadges = {};

const getTier = (tariff) => {
  // Если tier указан в тарифе, используем его
  if (tariff.tier) return String(tariff.tier).toLowerCase();
  // Иначе определяем автоматически по длительности (обратная совместимость)
  const duration = tariff.duration_days;
  if (duration >= 180) return 'elite';
  if (duration >= 90) return 'pro';
  return 'basic';
};

// Функция для правильного склонения слова "день/дня/дней" с учетом языка
const getDaysText = (count, language) => {
  if (language === 'ru') {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
      return 'дней';
    }
    if (lastDigit === 1) {
      return 'день';
    }
    if (lastDigit >= 2 && lastDigit <= 4) {
      return 'дня';
    }
    return 'дней';
  } else if (language === 'ua') {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
      return 'днів';
    }
    if (lastDigit === 1) {
      return 'день';
    }
    if (lastDigit >= 2 && lastDigit <= 4) {
      return 'дні';
    }
    return 'днів';
  } else if (language === 'cn') {
    return '天';
  } else {
    // English
    return count === 1 ? 'day' : 'days';
  }
};

// Функция для перевода названия тарифа (заменяет "Дней"/"дней"/"дня"/"день"/"Days"/"days"/"day" на переведенную версию)
const translateTariffName = (name, durationDays, language) => {
  if (!name) return name;
  
  // Паттерны для поиска слова "дней" в разных формах (русский/украинский)
  const ruUaPatterns = [
    /\d+\s+Дней/gi,
    /\d+\s+дней/gi,
    /\d+\s+дня/gi,
    /\d+\s+день/gi,
    /\d+\s+День/gi,
    /\d+\s+Дня/gi,
    /\d+\s+Днів/gi,
    /\d+\s+днів/gi,
    /\d+\s+дні/gi
  ];
  
  // Паттерны для английского
  const enPatterns = [
    /\d+\s+Days/gi,
    /\d+\s+days/gi,
    /\d+\s+Day/gi,
    /\d+\s+day/gi
  ];
  
  // Паттерны для китайского
  const cnPatterns = [
    /\d+\s+天/gi
  ];
  
  // Объединяем все паттерны
  const allPatterns = [...ruUaPatterns, ...enPatterns, ...cnPatterns];
  
  // Если название содержит число и слово дней/дня/день/Days/days/day/天, заменяем его
  for (const pattern of allPatterns) {
    if (pattern.test(name)) {
      const daysText = getDaysText(durationDays, language);
      // Заменяем найденное совпадение на число + переведенное слово
      // Для английского и китайского используем правильный регистр
      if (language === 'en') {
        return name.replace(pattern, `${durationDays} ${daysText.charAt(0).toUpperCase() + daysText.slice(1)}`);
      } else if (language === 'cn') {
        return name.replace(pattern, `${durationDays} ${daysText}`);
      } else {
        // Для русского и украинского - с заглавной буквы
        return name.replace(pattern, `${durationDays} ${daysText.charAt(0).toUpperCase() + daysText.slice(1)}`);
      }
    }
  }
  
  // Если паттерн не найден, возвращаем оригинальное название
  return name;
};

const TariffSection = () => {
  const { t, i18n } = useTranslation();
  const { currency, token, user } = useAuth();
  const { branding } = useBranding();
  const [tariffs, setTariffs] = useState([]);
  const [features, setFeatures] = useState(featurePresets); // Используем дефолтные, потом заменим из API
  const [tariffLevels, setTariffLevels] = useState([]); // уровни тарифов (basic/pro/elite + кастомные)
  const [loading, setLoading] = useState(true);
  const [loadingTariffId, setLoadingTariffId] = useState(null);
  const [error, setError] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, type, value, discount }
  const [promoError, setPromoError] = useState(null);
  const [selectedTariffId, setSelectedTariffId] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [createNewConfig, setCreateNewConfig] = useState(false);
  // Баланс конвертируется из USD в текущую валюту
  const userBalance = useMemo(() => {
    const balanceUSD = user?.balance_usd || user?.balance || 0;
    return convertBalanceFromUSD(balanceUSD, currency || user?.preferred_currency || 'uah');
  }, [user?.balance_usd, user?.balance, currency, user?.preferred_currency]);

  useEffect(() => {
    const fetchTariffs = async () => {
      setLoading(true);
      try {
        const [tariffsResponse, levelsResponse, featuresResponse, paymentMethodsResponse] = await Promise.all([
          fetch('/api/public/tariffs'),
          fetch('/api/public/tariff-levels', {
            cache: 'no-store',
            headers: { 'Cache-Control': 'no-cache' }
          }),
          fetch('/api/public/tariff-features', {
            cache: 'no-store',
            headers: {
              'Cache-Control': 'no-cache'
            }
          }),
          fetch('/api/public/available-payment-methods')
        ]);
        
        if (tariffsResponse.ok) {
          const tariffsData = await tariffsResponse.json();
          console.log('Loaded tariffs:', tariffsData); // Для отладки
          setTariffs(tariffsData);
        }

        if (levelsResponse.ok) {
          const levelsData = await levelsResponse.json();
          setTariffLevels(Array.isArray(levelsData) ? levelsData : []);
        } else {
          setTariffLevels([]);
        }
        
        if (featuresResponse.ok) {
          const featuresData = await featuresResponse.json();
          console.log('Loaded features from API:', featuresData);
          
          // Новый формат: объект { [tierCode]: [features...] }
          if (featuresData && typeof featuresData === 'object' && !Array.isArray(featuresData)) {
            setFeatures(featuresData);
          } else if (Array.isArray(featuresData)) {
            // Старый формат: массив объектов [{tier, features}]
            const merged = {};
            featuresData.forEach((item) => {
              if (!item?.tier) return;
              try {
                let parsed = item.features;
                if (typeof parsed === 'string') parsed = JSON.parse(parsed);
                if (Array.isArray(parsed)) merged[item.tier] = parsed;
              } catch (e) {
                console.error(`Error parsing features for tier ${item.tier}:`, e);
              }
            });
            setFeatures(Object.keys(merged).length ? merged : featurePresets);
          } else {
            setFeatures(featurePresets);
          }
        } else {
          console.warn('Failed to load features, using defaults');
          setFeatures(featurePresets);
        }
        
        if (paymentMethodsResponse.ok) {
          const paymentMethodsData = await paymentMethodsResponse.json();
          setAvailablePaymentMethods(paymentMethodsData.available_methods || []);
        }

        // Баланс пользователя
        if (token) {
          try {
            const userResponse = await fetch('/api/client/me', {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (userResponse.ok) {
              await userResponse.json();
              // Баланс теперь конвертируется автоматически через useMemo
            }
          } catch (e) {
            console.error('Ошибка загрузки баланса:', e);
          }
        }
      } catch (e) {
        console.error('Не удалось загрузить данные', e);
      } finally {
        setLoading(false);
      }
    };
    fetchTariffs();
  }, [token]);

  const orderedTierCodes = useMemo(() => {
    const levelsSorted = [...(tariffLevels || [])].sort(
      (a, b) => (a?.display_order ?? 0) - (b?.display_order ?? 0)
    );
    const codes = levelsSorted.map((l) => l?.code).filter(Boolean);
    return codes.length ? codes : ['basic', 'pro', 'elite'];
  }, [tariffLevels]);

  const baseTierCode = orderedTierCodes[0] || 'basic';

  const tierDisplayName = useCallback((tierCode) => {
    if (!tierCode) return '';
    const level = (tariffLevels || []).find((l) => l?.code === tierCode);
    if (level?.name) return level.name;
    // fallback на старые поля брендинга только для базовых уровней
    if (branding && tierCode && branding[`tariff_tier_${tierCode}_name`]) return branding[`tariff_tier_${tierCode}_name`];
    return tierCode;
  }, [tariffLevels, branding]);

  useEffect(() => {
    const fetchConfigs = async () => {
      if (!token) return;
      try {
        const resp = await fetch('/api/client/configs', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!resp.ok) return;
        const data = await resp.json();
        const cfgs = data.configs || [];
        setConfigs(cfgs);
        const primary = cfgs.find((c) => c.is_primary) || cfgs[0];
        if (primary && primary.id) {
          setSelectedConfigId(primary.id);
          setCreateNewConfig(false);
        }
      } catch (e) {
        console.error('Ошибка загрузки конфигов:', e);
      }
    };
    fetchConfigs();
  }, [token]);

  const intlFormatter = useMemo(() => {
    const config = currencyConfig[currency] || currencyConfig.uah;
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: config.code,
      maximumFractionDigits: 0
    });
  }, [currency]);

  const formatPrice = (tariff) => {
    const config = currencyConfig[currency] || currencyConfig.uah;
    const value = Number(tariff[config.field]) || 0;
    const formatted = intlFormatter.format(value);
    const perDay = tariff.duration_days ? value / tariff.duration_days : value;
    const perDayFormatted = new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: config.code,
      maximumFractionDigits: 2
    }).format(perDay);
    return { formatted, perDayFormatted, value };
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      setPromoError('Введите промокод');
      return;
    }
    
    setPromoError(null);
    try {
      const response = await fetch('/api/client/check-promocode', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ promo_code: promoCode.trim().toUpperCase() })
      });
      const data = await response.json();
      
      if (response.ok) {
        if (data.promo_type === 'PERCENT') {
          setAppliedPromo({ 
            code: data.code, 
            type: 'PERCENT', 
            value: data.value,
            discount: data.value 
          });
          setPromoError(null);
        } else if (data.promo_type === 'DAYS') {
          // Для бесплатных дней нужно активировать отдельно
          setPromoError('Промокод на бесплатные дни активируется на странице подписки');
          setAppliedPromo(null);
        }
      } else {
        setPromoError(data.message || 'Неверный промокод');
        setAppliedPromo(null);
      }
    } catch (e) {
      setPromoError('Ошибка проверки промокода');
      setAppliedPromo(null);
    }
  };

  const handleSelectTariff = (tariffId) => {
    setSelectedTariffId(tariffId);
    setShowPaymentModal(true);
  };

  const handleCreatePayment = async (paymentProvider) => {
    if (!selectedTariffId) return;
    
    // Если выбран баланс, используем специальный endpoint
    if (paymentProvider === 'balance') {
      setLoadingTariffId(selectedTariffId);
      setError(null);
      setShowPaymentModal(false);
      
      try {
        const response = await fetch('/api/client/purchase-with-balance', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            tariff_id: selectedTariffId,
            promo_code: appliedPromo && appliedPromo.type === 'PERCENT' ? appliedPromo.code : null,
            config_id: !createNewConfig ? selectedConfigId : null,
            create_new_config: createNewConfig
          })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Ошибка покупки тарифа');
        
        // Обновляем данные пользователя
        const userResponse = await fetch('/api/client/me?force_refresh=true', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (userResponse.ok) {
          const userData = await userResponse.json();
          if (userData.response) {
            // Обновляем пользователя через контекст, если доступен
            window.location.reload(); // Перезагружаем страницу для обновления данных
          }
        }
        
        alert('Тариф успешно активирован!');
        window.location.href = '/dashboard/subscription';
      } catch (e) {
        setError(e.message);
        setLoadingTariffId(null);
        setShowPaymentModal(true);
      }
      return;
    }
    
    setLoadingTariffId(selectedTariffId);
    setError(null);
    setShowPaymentModal(false);
    
    try {
      const response = await fetch('/api/client/create-payment', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          tariff_id: selectedTariffId,
          promo_code: appliedPromo && appliedPromo.type === 'PERCENT' ? appliedPromo.code : null,
          payment_provider: paymentProvider,
          config_id: !createNewConfig ? selectedConfigId : null,
          create_new_config: createNewConfig
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Ошибка создания счета');
      window.location.href = data.payment_url;
    } catch (e) {
      setError(e.message);
      setLoadingTariffId(null);
      setShowPaymentModal(true); // Показываем модалку снова при ошибке
    }
  };

  // Группируем тарифы по уровням (должно быть до условного возврата)
  const groupedTariffs = useMemo(() => {
    const groups = {};
    orderedTierCodes.forEach((code) => {
      groups[code] = [];
    });
    
    tariffs.forEach(tariff => {
      const tier = getTier(tariff);
      if (!groups[tier]) groups[tier] = [];
      groups[tier].push(tariff);
    });
    
    // Сортируем тарифы в каждой группе по длительности (от меньшего к большему)
    Object.keys(groups).forEach(tier => {
      groups[tier].sort((a, b) => (a.duration_days || 0) - (b.duration_days || 0));
    });
    
    return groups;
  }, [tariffs, orderedTierCodes]);

  if (loading) return <div className="loading-mini">Загружаем тарифы…</div>;

  const calculatePriceWithDiscount = (tariff) => {
    const price = formatPrice(tariff);
    if (appliedPromo && appliedPromo.type === 'PERCENT') {
      const discount = (appliedPromo.value / 100) * price.value;
      const finalPrice = price.value - discount;
      const config = currencyConfig[currency] || currencyConfig.uah;
      const intlFormatter = new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: config.code,
        maximumFractionDigits: 0
      });
      return {
        original: price.formatted,
        discounted: intlFormatter.format(finalPrice),
        discount: appliedPromo.value
      };
    }
    return null;
  };

  const renderTariffCard = (tariff) => {
    const isLoading = loadingTariffId === tariff.id;
    const tier = getTier(tariff);
    const price = formatPrice(tariff);
    const discountInfo = calculatePriceWithDiscount(tariff);
        return (
      <article key={tariff.id} className={`tariff-card ${tier} ${tier !== baseTierCode ? 'featured' : ''}`}>
        {tariff.badge && tariff.badge.trim() !== '' && (
          <span className="tariff-ribbon" title={t(`tariffs.badge.${tariff.badge}`, 'ТОП продаж')}>
            {t(`tariffs.badge.${tariff.badge}`, 'ТОП продаж')}
          </span>
        )}
        <p className="tariff-label">
          {tierDisplayName(tier) || (tier === 'basic' ? 'Базовый' : tier === 'pro' ? 'Премиум' : tier === 'elite' ? 'Элитный' : tier)}
        </p>
        <h3>{translateTariffName(t(`tariffs.${tariff.name}`, tariff.name), tariff.duration_days, i18n.resolvedLanguage || 'ru')}</h3>
        <div className="tariff-price">
          {discountInfo ? (
            <>
              <span style={{ textDecoration: 'line-through', opacity: 0.6, marginRight: '8px' }}>
                {discountInfo.original}
              </span>
              <span style={{ color: 'var(--accent-primary)', fontWeight: 'bold' }}>
                {discountInfo.discounted}
              </span>
              <span style={{ fontSize: '0.8em', color: 'var(--accent-primary)', marginLeft: '8px' }}>
                -{discountInfo.discount}%
              </span>
            </>
          ) : (
            price.formatted
          )}
          <span>/ {tariff.duration_days} {getDaysText(tariff.duration_days, i18n.resolvedLanguage || 'ru')}</span>
        </div>
        <div className="tariff-meta">
          <div>
            <span>Цена за день</span>
            <strong>{price.perDayFormatted}</strong>
          </div>
          {Number(tariff.bonus_days) > 0 && (
          <div>
            <span>Бонус</span>
              <strong>+{tariff.bonus_days} дней</strong>
          </div>
          )}
        </div>
        <ul className="tariff-features">
          {(features?.[tier] && Array.isArray(features[tier]) && features[tier].length > 0 ? features[tier] : (featurePresets[tier] || [])).map((feature, idx) => {
            // Используем кастомное название из брендинга, если есть
            const customName = branding?.tariff_features_names?.[feature] || feature;
            return <li key={`${tier}-${idx}-${feature}`}>{customName}</li>;
          })}
        </ul>
            <button 
          className="btn btn-primary"
              onClick={() => handleSelectTariff(tariff.id)}
              disabled={isLoading}
            >
          {isLoading ? 'Создаем счёт…' : t('tariffs.select')}
        </button>
      </article>
    );
  };

  return (
    <div className="tariffs-container">
      {error && <div className="message-error">{error}</div>}
      
      {/* Промокод */}
      <div className="promo-code-section">
        <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Промокод</h3>
        <div className="promo-code-input-wrapper">
          <input
            type="text"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            placeholder="Введите промокод"
            className="promo-code-input"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleApplyPromo();
              }
            }}
          />
          <button
            onClick={handleApplyPromo}
            className="btn promo-code-button"
          >
            Применить
          </button>
        </div>
        {promoError && (
          <div style={{ marginTop: '10px', color: 'var(--accent-red)', fontSize: '14px' }}>
            {promoError}
          </div>
        )}
        {appliedPromo && appliedPromo.type === 'PERCENT' && (
          <div style={{ marginTop: '10px', color: 'var(--accent-primary)', fontSize: '14px', fontWeight: '600' }}>
            ✓ Промокод {appliedPromo.code} применен! Скидка {appliedPromo.value}%
          </div>
        )}
      </div>
      
      {orderedTierCodes.map((tierCode) => {
        const list = groupedTariffs?.[tierCode] || [];
        if (!list.length) return null;
        return (
          <div key={tierCode} className="tariff-tier-section">
            <h3 className="tariff-tier-title">
              {tierDisplayName(tierCode) || tierCode}
            </h3>
            <div className="tariff-grid modern">
              {list.map(renderTariffCard)}
            </div>
          </div>
        );
      })}

      {/* Модальное окно выбора способа оплаты - рендерим через Portal в document.body */}
      {showPaymentModal && createPortal(
        <div 
          className="modal-overlay payment-modal-overlay" 
          onClick={() => setShowPaymentModal(false)}
        >
          <div 
            className="modal-content payment-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Выберите способ оплаты</h3>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                Конфиг для оплаты
              </div>
              <select
                value={createNewConfig ? 'new' : (selectedConfigId ?? '')}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'new') {
                    setCreateNewConfig(true);
                    setSelectedConfigId(null);
                  } else {
                    setCreateNewConfig(false);
                    setSelectedConfigId(v ? Number(v) : null);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  border: '1px solid var(--glass-border)',
                  background: 'var(--glass-bg)',
                  color: 'var(--text-primary)'
                }}
              >
                {configs.map((c) => (
                  <option key={c.id} value={c.id}>
                    {(c.is_primary ? '⭐ ' : '🧩 ') + (c.config_name || `Конфиг ${c.id}`)}
                  </option>
                ))}
                <option value="new">➕ Новый конфиг</option>
              </select>
            </div>
            <div className="payment-methods-list">
              {availablePaymentMethods.includes('crystalpay') && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleCreatePayment('crystalpay')}
                  disabled={loadingTariffId === selectedTariffId}
                >
                  {loadingTariffId === selectedTariffId ? 'Создаем счёт…' : 'CrystalPay'}
                </button>
              )}
              {availablePaymentMethods.includes('heleket') && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleCreatePayment('heleket')}
                  disabled={loadingTariffId === selectedTariffId}
                >
                  {loadingTariffId === selectedTariffId ? 'Создаем счёт…' : 'Heleket (Криптовалюты)'}
                </button>
              )}
              {availablePaymentMethods.includes('yookassa') && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleCreatePayment('yookassa')}
                  disabled={loadingTariffId === selectedTariffId}
                >
                  {loadingTariffId === selectedTariffId ? 'Создаем счёт…' : 'YooKassa (RUB)'}
                </button>
              )}
              {availablePaymentMethods.includes('yoomoney') && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleCreatePayment('yoomoney')}
                  disabled={loadingTariffId === selectedTariffId}
                >
                  {loadingTariffId === selectedTariffId ? 'Создаем счёт…' : 'YooMoney (RUB)'}
                </button>
              )}
              {availablePaymentMethods.includes('platega') && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleCreatePayment('platega')}
                  disabled={loadingTariffId === selectedTariffId}
                >
                  {loadingTariffId === selectedTariffId ? 'Создаем счёт…' : 'Platega'}
                </button>
              )}
              {availablePaymentMethods.includes('platega_mir') && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleCreatePayment('platega_mir')}
                  disabled={loadingTariffId === selectedTariffId}
                >
                  {loadingTariffId === selectedTariffId ? 'Создаем счёт…' : 'Карты МИР'}
                </button>
              )}
              {availablePaymentMethods.includes('mulenpay') && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleCreatePayment('mulenpay')}
                  disabled={loadingTariffId === selectedTariffId}
                >
                  {loadingTariffId === selectedTariffId ? 'Создаем счёт…' : 'Mulenpay'}
                </button>
              )}
              {availablePaymentMethods.includes('urlpay') && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleCreatePayment('urlpay')}
                  disabled={loadingTariffId === selectedTariffId}
                >
                  {loadingTariffId === selectedTariffId ? 'Создаем счёт…' : 'UrlPay'}
                </button>
              )}
              {availablePaymentMethods.includes('telegram_stars') && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleCreatePayment('telegram_stars')}
                  disabled={loadingTariffId === selectedTariffId}
                >
                  {loadingTariffId === selectedTariffId ? 'Создаем счёт…' : 'Telegram Stars ⭐'}
                </button>
              )}
              {availablePaymentMethods.includes('monobank') && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleCreatePayment('monobank')}
                  disabled={loadingTariffId === selectedTariffId}
                >
                  {loadingTariffId === selectedTariffId ? 'Создаем счёт…' : 'Monobank 💳'}
                </button>
              )}
              {availablePaymentMethods.includes('btcpayserver') && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleCreatePayment('btcpayserver')}
                  disabled={loadingTariffId === selectedTariffId}
                >
                  {loadingTariffId === selectedTariffId ? 'Создаем счёт…' : 'BTCPayServer ₿ (Bitcoin)'}
                </button>
              )}
              {availablePaymentMethods.includes('tribute') && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleCreatePayment('tribute')}
                  disabled={loadingTariffId === selectedTariffId}
                >
                  {loadingTariffId === selectedTariffId ? 'Создаем счёт…' : 'Tribute'}
                </button>
              )}
              {availablePaymentMethods.includes('robokassa') && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleCreatePayment('robokassa')}
                  disabled={loadingTariffId === selectedTariffId}
                >
                  {loadingTariffId === selectedTariffId ? 'Создаем счёт…' : 'Robokassa'}
                </button>
              )}
              {availablePaymentMethods.includes('freekassa') && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleCreatePayment('freekassa')}
                  disabled={loadingTariffId === selectedTariffId}
                >
                  {loadingTariffId === selectedTariffId ? 'Создаем счёт…' : 'Freekassa'}
                </button>
              )}
              {availablePaymentMethods.includes('kassa_ai') && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleCreatePayment('kassa_ai')}
                  disabled={loadingTariffId === selectedTariffId}
                >
                  {loadingTariffId === selectedTariffId ? 'Создаем счёт…' : 'Kassa AI'}
                </button>
              )}
              {/* Кнопка оплаты с баланса */}
              {selectedTariffId && (() => {
                const selectedTariff = tariffs.find(t => t.id === selectedTariffId);
                if (!selectedTariff) return null;
                const priceMap = {
                  uah: selectedTariff.price_uah,
                  rub: selectedTariff.price_rub,
                  usd: selectedTariff.price_usd
                };
                const userCurrency = user?.preferred_currency || currency || 'uah';
                const tariffPrice = priceMap[userCurrency] || priceMap.uah;
                const finalPrice = appliedPromo && appliedPromo.type === 'PERCENT' 
                  ? tariffPrice * (1 - appliedPromo.value / 100) 
                  : tariffPrice;
                const canAfford = userBalance >= finalPrice;
                
                return (
                  <div style={{marginTop: '15px', paddingTop: '15px', borderTop: '1px solid var(--glass-border)'}}>
                    <div style={{marginBottom: '10px', fontSize: '14px', color: 'var(--text-secondary)', textAlign: 'center'}}>
                      Баланс: <strong style={{color: canAfford ? 'var(--accent-green)' : 'var(--accent-red)'}}>
                        {userBalance.toFixed(2)} {userCurrency.toUpperCase()}
                      </strong>
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleCreatePayment('balance')}
                      disabled={loadingTariffId === selectedTariffId || !canAfford}
                      style={{
                        width: '100%',
                        backgroundColor: canAfford ? 'var(--accent-green)' : 'var(--accent-red)',
                        opacity: canAfford ? 1 : 0.6
                      }}
                    >
                      {loadingTariffId === selectedTariffId ? 'Обработка...' : 
                       canAfford ? `Оплатить с баланса (${finalPrice.toFixed(2)} ${userCurrency.toUpperCase()})` :
                       `Недостаточно средств (нужно ${finalPrice.toFixed(2)} ${userCurrency.toUpperCase()})`}
                    </button>
                  </div>
                );
              })()}
              {availablePaymentMethods.length === 0 && !selectedTariffId && (
                <p style={{ 
                  fontSize: '14px', 
                  color: 'var(--text-secondary)', 
                  textAlign: 'center',
                  padding: '20px'
                }}>
                  Нет доступных способов оплаты. Обратитесь к администратору.
                </p>
              )}
              <button
                className="btn payment-cancel-button"
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedTariffId(null);
                }}
                disabled={loadingTariffId === selectedTariffId}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default function ClientTariffsPage() {
    const { t } = useTranslation();
    const { branding } = useBranding();

    const tariffsTitle = branding?.dashboard_tariffs_title || 'Премиум VPN без лимитов';
    const tariffsDescription = branding?.dashboard_tariffs_description || 'Выберите срок подписки и мгновенно получите доступ к всем серверам и новому ключу подключения.';

    return (
        <main className="client-page-content">
      <section className="tariff-hero glass-card gradient-card">
        <div>
          <p className="eyebrow-text">{t('dashboard.tariffs')}</p>
          <h2>{tariffsTitle}</h2>
          <p>{tariffsDescription}</p>
        </div>
        <ul className="tariff-hero-stats">
          <li><span>5+</span> способов оплаты</li>
          <li><span>24/7</span> поддержка</li>
          <li><span>∞</span> трафик</li>
        </ul>
      </section>

            <section className="tariffs-section">
                <TariffSection />
            </section>
        </main>
    );
}
